import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import classNames from 'classnames';
import { NumberInput } from '@carbon/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { chartGridAtom, chartsAtom } from '../atoms';
import { ChartCard } from './chart-card';
import styles from '../eval3-new-content.module.scss';

/**
 * The Charts & Visuals work-area output section — the READ side of the charts feature,
 * isolated from the `Eval3NewContent` root so chart-state churn stays contained. It
 * subscribes to `chartsAtom` and reads/writes the grid's Rows/Columns via `chartGridAtom`, so
 * adding / removing / renaming / toggling a chart (or changing a grid dimension) re-renders
 * only this subtree — NOT the map / table / summary / panels the feature root also mounts.
 * (The write side — the Add/rename/visibility manager — lives in `ChartsVisualsSection`; this
 * is its output counterpart.)
 *
 * Renders nothing until at least one chart is visible; the chart grid maps each visible chart
 * to a `ChartCard` keyed on its `instanceId`.
 *
 * @return {JSX.Element | null} The charts accordion, or null when no chart is visible.
 */
export function ChartsWorkArea() {
  // The charts toggled visible in the left-panel Charts & Visuals manager (`chartsAtom`).
  // This subscription lives HERE (not the feature root) so its churn doesn't re-render the
  // whole eval3 tree.
  const visibleCharts = useAtomValue(chartsAtom).filter(
    (chart) => chart.visible
  );
  // A stable setter for persisting a custom chart's edited spec back to its instance (no
  // subscription — this doesn't re-render on chart-list churn).
  const dispatchCharts = useSetAtom(chartsAtom);
  // Chart-grid dimensions (mock §Charts) — now in `chartGridAtom` so they seed from the
  // persisted `chart_config` on load and feed the autosave. `columns` drives the grid's
  // column count; `rows` sets the explicit row tracks. Clamped 1–4.
  const [grid, setGrid] = useAtom(chartGridAtom);
  const clampDimension = (value: number) => Math.min(4, Math.max(1, value));

  // The section appears only when a chart is visible (Add Chart → check a chart reveals it).
  if (visibleCharts.length === 0) {
    return null;
  }

  return (
    <Accordion className={styles.chartsAccordion} defaultExpanded>
      <AccordionSummary className={styles.chartsSummary}>
        <span className={styles.chartsTitle}>Charts & Visuals</span>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.chartsBody}>
          <div className={styles.chartsControls}>
            <NumberInput
              id="eval3-charts-rows"
              label="Rows"
              min={1}
              max={4}
              size="sm"
              value={grid.rows}
              onChange={(_event, { value }) => {
                // Carbon emits '' on a cleared field; keep the current dimension until a
                // real number is entered. `clampDimension` still guards the grid-track
                // classes (`.chartsGridRows{n}` exist only for 1–4) against a typed value
                // outside min/max.
                if (value !== '') {
                  setGrid((current) => ({
                    ...current,
                    rows: clampDimension(Number(value)),
                  }));
                }
              }}
            />
            <NumberInput
              id="eval3-charts-columns"
              label="Columns"
              min={1}
              max={4}
              size="sm"
              value={grid.columns}
              onChange={(_event, { value }) => {
                if (value !== '') {
                  setGrid((current) => ({
                    ...current,
                    columns: clampDimension(Number(value)),
                  }));
                }
              }}
            />
          </div>
          <div
            className={classNames(
              styles.chartsGrid,
              styles[`chartsGridColumns${grid.columns}`],
              styles[`chartsGridRows${grid.rows}`]
            )}
          >
            {visibleCharts.map((chart) => (
              <ChartCard
                key={chart.instanceId}
                source={chart.source}
                title={chart.name}
                spec={chart.spec}
                toggle={chart.toggle}
                // Register this (visible → mounted) chart for content export. The id is the
                // ZIP `charts[key]` filename, so we prefer the readable catalog id over the
                // instance uuid. This is unique TODAY because the Add-Chart picker enforces
                // one instance per catalog preset (see ChartsVisualsSection); if that ever
                // relaxes to multi-instance, this key must gain per-instance disambiguation
                // (the registry Map + the ZIP both key on it), else two instances collide.
                // Only visible charts reach here, so the registry mirrors what's rendered.
                registerExport={{
                  id: chart.catalogId ?? chart.instanceId,
                  name: chart.name,
                  group: 'chartsVisuals',
                }}
                // Custom charts persist their edited (filter-free) spec back to the instance;
                // the chart-config autosave then saves it. Catalog charts are read-only.
                onSpecChange={
                  chart.source === 'custom'
                    ? (spec) =>
                        dispatchCharts({
                          type: 'update',
                          chart: { ...chart, spec },
                        })
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
